import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Beaker, Camera, Sliders, Lightbulb, Crown, CheckCircle, Info, AlertCircle, BookOpen } from 'lucide-react';

export function AnalysisGuide() {
  const [expandedSection, setExpandedSection] = useState<string | null>('overview');

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const Section = ({ id, title, children }: { id: string; title: string; children: React.ReactNode }) => (
    <div className="border border-white/10 rounded-xl overflow-hidden mb-4">
      <button
        onClick={() => toggleSection(id)}
        className="w-full flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 transition-colors"
      >
        <h3 className="text-lg text-white font-medium">{title}</h3>
        {expandedSection === id ? (
          <ChevronUp className="w-5 h-5 text-white/70" />
        ) : (
          <ChevronDown className="w-5 h-5 text-white/70" />
        )}
      </button>
      {expandedSection === id && (
        <div className="p-4 bg-white/[0.02] space-y-4">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl text-white font-brand font-light italic mb-2">Bread Analysis</h2>
        <p className="text-white/80">
          Get feedback on your bakes to understand what worked well and what to improve next time.
        </p>
      </div>

      <Section id="overview" title="What is Bread Analysis?">
        <p className="text-white/80 mb-4">
          Bread analysis is a comprehensive evaluation of your baking results. Using photos of your bread and information
          about your process, Breadlify provides personalized feedback on what went well and specific recommendations
          for improvement.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Camera className="w-5 h-5 text-blue-400" />
              <h4 className="text-white font-medium">Visual Assessment</h4>
            </div>
            <p className="text-white/70 text-sm">
              Upload photos of your bread's exterior and crumb. Breadlify analyzes color, structure, and appearance.
            </p>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Sliders className="w-5 h-5 text-green-400" />
              <h4 className="text-white font-medium">Self-Rating</h4>
            </div>
            <p className="text-white/70 text-sm">
              Rate different aspects of your bread to help the system understand your perspective and priorities.
            </p>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Beaker className="w-5 h-5 text-purple-400" />
              <h4 className="text-white font-medium">Process Data</h4>
            </div>
            <p className="text-white/70 text-sm">
              Input your baking parameters so Breadlify can correlate your process with the results.
            </p>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Lightbulb className="w-5 h-5 text-amber-400" />
              <h4 className="text-white font-medium">Actionable Feedback</h4>
            </div>
            <p className="text-white/70 text-sm">
              Receive specific, actionable recommendations tailored to your situation and skill level.
            </p>
          </div>
        </div>
      </Section>

      <Section id="creating" title="Creating a New Analysis">
        <p className="text-white/80 mb-4">
          To analyze your bread, click "NEW ANALYSIS" in the Analysis tab. The process has 4 steps:
        </p>

        <div className="space-y-4">
          <div className="bg-white/5 rounded-lg p-4 border-l-4 border-blue-400">
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-blue-500/20 rounded-full flex items-center justify-center text-sm text-blue-400 font-bold">1</span>
              Upload Photos
            </h4>

            <div className="space-y-3 ml-9">
              <p className="text-white/70 text-sm">
                Upload clear, well-lit photos of your bread. The quality of your photos directly affects the quality
                of the analysis.
              </p>

              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-white/90 font-medium text-sm mb-2">Photo Tips:</p>
                <ul className="text-white/60 text-xs space-y-1.5">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Take photos in natural daylight or bright, even lighting</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Capture the whole loaf for exterior shots</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>For crumb shots, take photos of a clean, straight slice</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Avoid shadows and use a neutral background</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Make sure the photo is in focus and not blurry</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Upload at least 2 photos: one of the crust, one of the crumb</span>
                  </li>
                </ul>
              </div>

              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-amber-200 text-xs">
                    Better photos = better analysis! Take your time to capture your bread properly.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4 border-l-4 border-green-400">
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-green-500/20 rounded-full flex items-center justify-center text-sm text-green-400 font-bold">2</span>
              Rate Your Bread
            </h4>

            <p className="text-white/70 text-sm mb-3 ml-9">
              Use the sliders to rate different aspects of your bread. Be honest - this helps the AI provide better feedback!
            </p>

            <div className="space-y-3 ml-9">
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-white/90 font-medium text-sm mb-1">Crumb Structure (0-100)</p>
                <p className="text-white/60 text-xs">
                  How open and airy is your crumb? Rate the hole size, distribution, and overall structure.
                </p>
                <ul className="text-white/50 text-xs mt-2 space-y-0.5 ml-3">
                  <li>• 0-30: Dense, few holes, tight crumb</li>
                  <li>• 30-60: Some holes, moderate openness</li>
                  <li>• 60-100: Very open, large irregular holes, artisan quality</li>
                </ul>
              </div>

              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-white/90 font-medium text-sm mb-1">Oven Spring (0-100)</p>
                <p className="text-white/60 text-xs">
                  How much did your bread rise in the oven? Rate the height and volume.
                </p>
                <ul className="text-white/50 text-xs mt-2 space-y-0.5 ml-3">
                  <li>• 0-30: Flat, minimal rise, spreading loaf</li>
                  <li>• 30-60: Moderate rise, good volume</li>
                  <li>• 60-100: Dramatic rise, excellent lift, bold ear</li>
                </ul>
              </div>

              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-white/90 font-medium text-sm mb-1">Crust Quality (0-100)</p>
                <p className="text-white/60 text-xs">
                  Rate the color, thickness, and crispness of your crust.
                </p>
                <ul className="text-white/50 text-xs mt-2 space-y-0.5 ml-3">
                  <li>• 0-30: Pale, soft, or burnt crust</li>
                  <li>• 30-60: Decent color, some crispness</li>
                  <li>• 60-100: Deep golden-brown, crispy, perfect blistering</li>
                </ul>
              </div>

              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-white/90 font-medium text-sm mb-1">Crumb Texture (0-100)</p>
                <p className="text-white/60 text-xs">
                  How does the interior feel? Rate moistness, chewiness, and overall texture.
                </p>
                <ul className="text-white/50 text-xs mt-2 space-y-0.5 ml-3">
                  <li>• 0-30: Dry, gummy, or rubbery texture</li>
                  <li>• 30-60: Good texture with minor issues</li>
                  <li>• 60-100: Perfect moisture, tender but structured, delightful chew</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4 border-l-4 border-purple-400">
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-purple-500/20 rounded-full flex items-center justify-center text-sm text-purple-400 font-bold">3</span>
              Enter Baking Parameters
            </h4>

            <p className="text-white/70 text-sm mb-3 ml-9">
              Provide details about your process so the system can connect your methods to your results:
            </p>

            <div className="space-y-2 ml-9 text-sm">
              <div className="flex items-start gap-2 text-white/70">
                <span className="text-amber-400">•</span>
                <span><span className="text-white font-medium">Fermentation Type:</span> Which method you used (direct, cold bulk, etc.)</span>
              </div>
              <div className="flex items-start gap-2 text-white/70">
                <span className="text-amber-400">•</span>
                <span><span className="text-white font-medium">Fermentation Duration:</span> Total time from mixing to baking</span>
              </div>
              <div className="flex items-start gap-2 text-white/70">
                <span className="text-amber-400">•</span>
                <span><span className="text-white font-medium">Hydration:</span> Water percentage in your dough (typically 65-85%)</span>
              </div>
              <div className="flex items-start gap-2 text-white/70">
                <span className="text-amber-400">•</span>
                <span><span className="text-white font-medium">Baking Method:</span> Dutch oven, baking stone, pan, etc.</span>
              </div>
              <div className="flex items-start gap-2 text-white/70">
                <span className="text-amber-400">•</span>
                <span><span className="text-white font-medium">Temperature:</span> Oven temperature in Celsius</span>
              </div>
              <div className="flex items-start gap-2 text-white/70">
                <span className="text-amber-400">•</span>
                <span><span className="text-white font-medium">Baking Duration:</span> How long it was in the oven</span>
              </div>
              <div className="flex items-start gap-2 text-white/70">
                <span className="text-amber-400">•</span>
                <span><span className="text-white font-medium">Weight:</span> Final bread weight in kg</span>
              </div>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 mt-3 ml-9">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-blue-200 text-xs">
                  The more accurate your parameters, the better we can identify what worked and what didn't.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4 border-l-4 border-amber-400">
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-amber-500/20 rounded-full flex items-center justify-center text-sm text-amber-400 font-bold">4</span>
              Review Feedback
            </h4>

            <p className="text-white/70 text-sm mb-3 ml-9">
              After processing, you'll receive comprehensive feedback including:
            </p>

            <div className="space-y-3 ml-9">
              <div className="bg-white/5 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb className="w-4 h-4 text-yellow-400" />
                  <p className="text-white/90 font-medium text-sm">Main Issue Identified</p>
                </div>
                <p className="text-white/60 text-xs">
                  Breadlify identifies the primary area needing improvement based on your ratings and photos
                  (e.g., "underfermentation", "overproofing", "shaping issues").
                </p>
              </div>

              <div className="bg-white/5 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Beaker className="w-4 h-4 text-purple-400" />
                  <p className="text-white/90 font-medium text-sm">Methodology Analysis</p>
                </div>
                <p className="text-white/60 text-xs">
                  Detailed explanation of what likely caused the issue, connecting your process parameters
                  to the outcome you observed.
                </p>
              </div>

              <div className="bg-white/5 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <p className="text-white/90 font-medium text-sm">Specific Improvements</p>
                </div>
                <p className="text-white/60 text-xs">
                  Actionable recommendations for your next bake, such as:
                </p>
                <ul className="text-white/50 text-xs mt-2 space-y-1 ml-3">
                  <li>• "Extend bulk fermentation by 1-2 hours"</li>
                  <li>• "Increase hydration to 75-78%"</li>
                  <li>• "Practice gentle shaping techniques"</li>
                  <li>• "Lower oven temperature to 230°C"</li>
                </ul>
              </div>

              <div className="bg-white/5 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen className="w-4 h-4 text-blue-400" />
                  <p className="text-white/90 font-medium text-sm">Knowledge Base Links</p>
                </div>
                <p className="text-white/60 text-xs">
                  Direct links to relevant articles in the Knowledge section where you can learn more about
                  the techniques and concepts mentioned in the feedback.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section id="interpreting" title="Interpreting Your Analysis">
        <p className="text-white/80 mb-4">
          Understanding your analysis results helps you make targeted improvements:
        </p>

        <div className="space-y-3">
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2">Common Issues & What They Mean</h4>
            <div className="space-y-3 mt-3">
              <div className="border-l-2 border-red-400 pl-3">
                <p className="text-white font-medium text-sm">Underfermentation</p>
                <p className="text-white/60 text-xs mt-1">
                  Dense crumb, poor oven spring. Solution: Ferment longer or use warmer temperatures.
                </p>
              </div>
              <div className="border-l-2 border-orange-400 pl-3">
                <p className="text-white font-medium text-sm">Overfermentation</p>
                <p className="text-white/60 text-xs mt-1">
                  Flat loaf, excessive sourness, very open but weak structure. Solution: Reduce fermentation time.
                </p>
              </div>
              <div className="border-l-2 border-yellow-400 pl-3">
                <p className="text-white font-medium text-sm">Shaping Issues</p>
                <p className="text-white/60 text-xs mt-1">
                  Irregular shape, poor surface tension. Solution: Practice shaping techniques, handle dough more gently.
                </p>
              </div>
              <div className="border-l-2 border-blue-400 pl-3">
                <p className="text-white font-medium text-sm">Baking Problems</p>
                <p className="text-white/60 text-xs mt-1">
                  Pale or burnt crust, gummy interior. Solution: Adjust oven temperature, baking time, or steam levels.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2">Chart Visualization</h4>
            <p className="text-white/70 text-sm">
              The radar chart shows your bread's performance across all four dimensions. Look for:
            </p>
            <ul className="text-white/60 text-sm mt-2 space-y-1 ml-4">
              <li>• Balanced shapes = well-rounded loaf with no major weak points</li>
              <li>• Lopsided shapes = specific areas needing focus</li>
              <li>• Small overall area = multiple aspects need improvement</li>
              <li>• Large area = you're doing great overall!</li>
            </ul>
          </div>
        </div>
      </Section>

      <Section id="viewing" title="Viewing Past Analyses">
        <p className="text-white/80 mb-4">
          All your analyses are saved and accessible in the Analysis tab:
        </p>

        <ul className="space-y-3 text-white/70 text-sm">
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Track Progress Over Time</span>
              Compare your recent analyses to see how your skills improve with each bake.
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Review Recommendations</span>
              Go back to previous feedback to remind yourself of techniques you wanted to try.
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Compare Methods</span>
              See how different fermentation types or parameters affected your results.
            </div>
          </li>
        </ul>
      </Section>

      <Section id="tips" title="Getting the Most from Analysis">
        <ul className="space-y-3 text-white/70 text-sm">
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Analyze every bake</span>
              The more data points you have, the better you can track your progress and identify patterns.
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Be honest with ratings</span>
              Don't inflate your ratings - accurate self-assessment leads to better feedback.
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Take good photos</span>
              Invest 2 minutes in proper photography. Good lighting and clear shots make a huge difference.
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Follow up on recommendations</span>
              Implement one or two suggestions per bake rather than trying to change everything at once.
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Use knowledge base links</span>
              Click through to learn more about the techniques mentioned. Understanding the "why" helps you improve faster.
            </div>
          </li>
        </ul>
      </Section>
    </div>
  );
}
